/**
 * Portal API wrapper (F5, PLAN.md §4.2) — deliberately NOT `@/shared/lib/api`.
 *
 * The portal is a separate auth surface (`vanagreen_partner_session` /
 * `vanagreen_partner_csrf` cookies, distinct from the operator app's
 * `vanagreen_session` / `vanagreen_csrf`) — sharing the operator client
 * would also share its global 401 handler, so a portal session expiry
 * would incorrectly redirect the operator app (or vice versa). Same
 * response envelope convention as the shared client.
 */
import { getCookie } from '@/shared/lib/api';

const BASE_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');
const PARTNER_CSRF_COOKIE_NAME = 'vanagreen_partner_csrf';
const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export class PortalApiError extends Error {
  public readonly code: string;
  public readonly status: number;

  constructor(opts: { code: string; message: string; status: number }) {
    super(opts.message);
    this.name = 'PortalApiError';
    this.code = opts.code;
    this.status = opts.status;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | undefined>;
}

async function portalApi<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (options.query) {
    for (const [key, value] of Object.entries(options.query)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }
  const method = (options.method ?? 'GET').toUpperCase();
  const csrfHeaders: Record<string, string> = {};
  if (UNSAFE_METHODS.has(method)) {
    const csrf = getCookie(PARTNER_CSRF_COOKIE_NAME);
    if (csrf) csrfHeaders['x-csrf-token'] = csrf;
  }

  const response = await fetch(url.toString(), {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...csrfHeaders,
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  const text = await response.text();
  if (!response.ok) {
    let code = 'ERR_UNKNOWN';
    let message = text || 'Unknown error';
    try {
      const parsed = JSON.parse(text) as { error?: { code?: string; message?: string } };
      if (parsed.error) {
        code = parsed.error.code ?? code;
        message = parsed.error.message ?? message;
      }
    } catch {
      // not JSON, keep raw text
    }
    throw new PortalApiError({ code, message, status: response.status });
  }
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

export const portalHttp = {
  get: <T>(path: string, query?: RequestOptions['query']) => portalApi<T>(path, { query }),
  post: <T>(path: string, body?: unknown) => portalApi<T>(path, { method: 'POST', body }),
};
